/*
 * Ячейки таблиц кластеров и моделей. Презентационные компоненты получают уже
 * загруженные данные (главная страница грузит их пачкой и сортирует «развёрнутое
 * первым»), Entity*-обёртки грузят данные сами — для страниц каталога, где строки
 * отдаёт CatalogTable.
 */
import { ReactNode } from 'react';
import { Entity } from '@backstage/catalog-model';
import { useApi, useApp } from '@backstage/core-plugin-api';
import { Link, StatusWarning } from '@backstage/core-components';
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import useAsync from 'react-use/esm/useAsync';
import { MlflowApi, mlflowApiRef } from '@internal/plugin-mlflow';
import { ArgoAppsSummary, ServingSummary, useArgoApps } from '../argocd';
import {
  CLUSTER_SERVICE_LINKS,
  DATASTORE_CLUSTER_ANNOTATION,
  DEFAULT_CHAMPION_ALIAS,
  MLFLOW_CHAMPION_ALIAS_ANNOTATION,
  MLFLOW_MODEL_NAME_ANNOTATION,
  MLFLOW_PRIMARY_METRIC_ANNOTATION,
  annotation,
} from '../../lib/viva';

export type ModelSummary = Awaited<ReturnType<MlflowApi['getModelSummary']>>;

/** Состояние загрузки ячейки: совместимо с AsyncState react-use и с результатом settle(). */
export type CellState<T> = { loading: boolean; value?: T; error?: Error };

/** Promise -> CellState: ошибка одной ячейки не должна ронять всю таблицу. */
export async function settle<T>(promise: Promise<T>): Promise<CellState<T>> {
  try {
    return { loading: false, value: await promise };
  } catch (e) {
    return { loading: false, error: e as Error };
  }
}

/** Загрузка / ошибка / значение для ячейки; ошибка показывается в самой ячейке. */
export function AsyncCell<T>({
  state,
  unavailable,
  children,
}: {
  state: CellState<T>;
  unavailable: string;
  children: (value: T) => ReactNode;
}) {
  if (state.loading) {
    return <CircularProgress size={16} />;
  }
  if (state.error) {
    return (
      <Tooltip title={state.error.message}>
        <span style={{ whiteSpace: 'nowrap' }}>
          <StatusWarning>{unavailable}</StatusWarning>
        </span>
      </Tooltip>
    );
  }
  return <>{children(state.value as T)}</>;
}

const Muted = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" color="textSecondary" noWrap>
    {children}
  </Typography>
);

const useServiceLinkStyles = makeStyles(theme => ({
  root: {
    display: 'inline-flex',
    gap: theme.spacing(0.5),
    whiteSpace: 'nowrap',
  },
  button: {
    padding: 4,
  },
}));

/**
 * Ссылки кластера на его сервисы (ArgoCD, Grafana, MLflow, Vault) из metadata.links —
 * иконками, как на карточке ссылок: имя иконки берётся из links[].icon через реестр
 * иконок приложения (cloud, dashboard, catalog, lock, kubernetes, ml-model, …).
 */
export const ServiceLinks = ({ entity }: { entity: Entity }) => {
  const classes = useServiceLinkStyles();
  const app = useApp();
  const links = (entity.metadata.links ?? []).filter(link =>
    CLUSTER_SERVICE_LINKS.includes(link.title ?? ''),
  );
  if (links.length === 0) {
    return <Muted>—</Muted>;
  }
  return (
    <span className={classes.root}>
      {links.map(link => {
        const Icon = (link.icon && app.getSystemIcon(link.icon)) || LinkIcon;
        return (
          <Tooltip key={link.url} title={link.title ?? link.url}>
            <IconButton
              component={Link}
              to={link.url}
              className={classes.button}
              aria-label={link.title ?? link.url}
              color="primary"
            >
              <Icon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      })}
    </span>
  );
};

/** Версия-чемпион и её главная метрика по данным реестра MLflow. */
export const ChampionCell = ({
  entity,
  summary,
}: {
  entity: Entity;
  summary?: ModelSummary;
}) => {
  if (!summary) {
    return <Muted>—</Muted>;
  }
  const alias =
    annotation(entity, MLFLOW_CHAMPION_ALIAS_ANNOTATION) ??
    DEFAULT_CHAMPION_ALIAS;
  const version = summary.aliases[alias];
  if (!version) {
    return <Muted>нет @{alias}</Muted>;
  }
  const metricKey = annotation(entity, MLFLOW_PRIMARY_METRIC_ANNOTATION);
  const metric = metricKey
    ? summary.versions
        .find(v => v.version === version)
        ?.run?.metrics.find(m => m.key === metricKey)
    : undefined;
  const label = `v${version}`;
  return (
    <Typography variant="body2" noWrap>
      {summary.url ? <Link to={summary.url}>{label}</Link> : label}
      {metric && (
        <Typography
          component="span"
          variant="body2"
          color="textSecondary"
        >{` · ${metric.key} ${metric.value.toFixed(3)}`}</Typography>
      )}
    </Typography>
  );
};

export const EntityChampionCell = ({ entity }: { entity: Entity }) => {
  const api = useApi(mlflowApiRef);
  const modelName = annotation(entity, MLFLOW_MODEL_NAME_ANNOTATION);
  // MLflow кластера модели; без аннотации — mlflow.baseUrl
  const cluster = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
  const state = useAsync(
    async () =>
      modelName ? api.getModelSummary(modelName, cluster) : undefined,
    [api, modelName, cluster],
  );
  return (
    <AsyncCell state={state} unavailable="MLflow недоступен">
      {summary => <ChampionCell entity={entity} summary={summary} />}
    </AsyncCell>
  );
};

export const EntityServingCell = ({ entity }: { entity: Entity }) => {
  const state = useArgoApps(entity);
  return (
    <AsyncCell state={state} unavailable="ArgoCD недоступен">
      {apps => (
        <ServingSummary apps={apps ?? []} modelName={entity.metadata.name} />
      )}
    </AsyncCell>
  );
};

export const EntityArgoAppsCell = ({ entity }: { entity: Entity }) => {
  const state = useArgoApps(entity);
  return (
    <AsyncCell state={state} unavailable="ArgoCD недоступен">
      {apps => <ArgoAppsSummary apps={apps ?? []} />}
    </AsyncCell>
  );
};
